<template>
  <div style="padding: 20px">
    <v-dialog v-model="etsy_data_dialog" scrollable max-width="800px">
      <v-card style="padding: 20px; height: 100%">
        <div v-if="fetching_etsy_data" style="padding: 60px 0px" class="text-center">
          <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
        </div>
        <div v-else>
          <div class="mt-5 mb-5 text-center" v-if="!etsy_data.success">
            <h2>Something went wrong!!</h2>
          </div>
          <div v-else>
            <json-viewer :value="etsy_data.response" copyable expanded></json-viewer>
          </div>
        </div>
      </v-card>
    </v-dialog>
    
    <!-- Image Difference Modal -->
    <v-dialog v-model="imageDifferenceDialog" scrollable max-width="900px">
      <v-card>
        <v-card-title class="headline">
          Realtime Image Comparison
          <v-spacer></v-spacer>
          <v-btn icon @click="imageDifferenceDialog = false">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="loadingImages" style="padding: 60px 0px" class="text-center">
            <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
            <p class="mt-2">Loading realtime image comparison data...</p>
          </div>
          <div v-else-if="!imageData.success" class="text-center py-4">
            <v-icon large color="error">{{ icons.mdiAlertCircle }}</v-icon>
            <p class="mt-2">{{ imageData.message || 'Failed to load realtime image comparison' }}</p>
          </div>
          <div v-else>
            <v-row class="mb-4">
              <v-col cols="6" class="text-center">
                <h3>Etsy Images ({{ imageData.data.etsy_image_count }})</h3>
              </v-col>
              <v-col cols="6" class="text-center">
                <h3>Shopify Images ({{ imageData.data.shopify_image_count }})</h3>
              </v-col>
            </v-row>
            
            <div v-for="(_, index) in Math.max(imageData.data.etsy_images.length, imageData.data.shopify_images.length)" :key="index" class="mb-6">
              <v-row>
                <v-col cols="6" class="text-center">
                  <v-card outlined v-if="index < imageData.data.etsy_images.length" class="mx-auto" max-width="400">
                    <v-img
                      :src="imageData.data.etsy_images[index].url"
                      max-height="300"
                      contain
                      class="grey lighten-2"
                    ></v-img>
                    <v-card-text class="text-center">
                      Image #{{ index + 1 }}
                    </v-card-text>
                  </v-card>
                  <v-card v-else outlined class="mx-auto pa-4 grey lighten-3" max-width="400" height="300">
                    <div class="text-center my-12">
                      <v-icon large>mdi-image-off</v-icon>
                      <p>No image</p>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="6" class="text-center">
                  <v-card outlined v-if="index < imageData.data.shopify_images.length" class="mx-auto" max-width="400">
                    <v-img
                      :src="imageData.data.shopify_images[index].url"
                      max-height="300"
                      contain
                      class="grey lighten-2"
                    ></v-img>
                    <v-card-text class="text-center">
                      Image #{{ index + 1 }}
                    </v-card-text>
                  </v-card>
                  <v-card v-else outlined class="mx-auto pa-4 grey lighten-3" max-width="400" height="300">
                    <div class="text-center my-12">
                      <v-icon large>mdi-image-off</v-icon>
                      <p>No image</p>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-data-table :headers="headers" :items="products.data" class="elevation-1" hide-default-footer>
      <template v-slot:top>
        <v-row style="padding: 10px">
          <v-col md="2" cols="6"> <strong>Total: </strong> {{ products.total }} </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="searchTerm"
              label="Search Keyword"
              outlined
              dense
              placeholder="Search Keyword"
              @change="fetchEtsyProducts()"
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </template>
      <template v-slot:body="{ items }">
        <tbody v-if="!is_loading">
          <tr v-if="!items.length">
            <td colspan="3" class="text-center" style="padding: 20px 0px">
              <img :src="require('@/assets/images/misc/no-data.png')" alt="" width="250px" />
              <p style="font-size: 25px">No Data Found!!</p>
            </td>
          </tr>
          <tr v-for="item in items" :key="item.id" v-else>
            <td style="padding: 10px; width: 45%" :set="(etsy_images = JSON.parse(item.fetched_imagesdata))">
              <div style="display: flex">
                <img
                  :src="
                    etsy_images && etsy_images[0] && etsy_images[0].url_fullxfull
                      ? etsy_images[0].url_fullxfull
                      : require('@/assets/images/misc/no-image.png')
                  "
                  width="100px"
                  height="100px"
                  alt=""
                  class="mr-3"
                />
                <div>
                  <span class="primary--text" @click="etsyProductDetail(item.id)">{{ item.title }}</span>
                  <p class="mt-2"><strong>Listing ID:</strong> {{ item.listing_id }}</p>
                  <p class="mt-2"><strong>SKU: <span v-html="showSKUStatus(item)"></span></strong></p>
                </div>
              </div>
            </td>
            <td style="padding: 10px; width: 45%" :set="(shopify_attrs = item.shopify_product != null && JSON.parse(item.shopify_product.attributes))">
              <div style="display: flex">
                <img
                  :src="
                    shopify_attrs.image_url ? shopify_attrs.image_url : require('@/assets/images/misc/no-image.png')
                  "
                  width="100px"
                  height="100px"
                  alt=""
                  class="mr-3"
                />
                <div>
                  <span class="primary--text" @click="shopifyProductDetail(item.shopify_product.id)">{{ item.title }}</span>
                  <p class="mt-2"><strong>Shopify ID:</strong> {{ item.shopify_product && item.shopify_product.shopify_id }}</p>
                </div>
              </div>
            </td>
            <td>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="info" small class="ml-1" v-bind="attrs" v-on="on">
                    <v-icon small>{{ icons.mdiMenu }}</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="showImageDifference(item)">
                    <v-list-item-title>Realtime Image Comparison</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="getEtsyApiDetail(item.listing_id)">
                    <v-list-item-title>View Raw</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="6" class="text-center" style="padding: 80px 0px">
              <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <v-pagination
      v-model="products.current_page"
      class="my-4"
      @input="handlePageChange"
      :length="products.last_page"
      :total-visible="7"
    ></v-pagination>
  </div>
</template>
<script>
import store from '@/store'
import JsonViewer from 'vue-json-viewer'
import router from '@/router'
import { mdiClose, mdiAlertCircle, mdiMenu } from '@mdi/js'
  export default {
  props: {
    search: String,
  },
  data() {
    return {
      shop: null,
      is_loading: false,
      configurations: {},
      products: {
        current_page: 1,
        data: [],
      },
      headers: [{ text: 'Etsy Product', value: 'title' }, { text: 'Shopify Product' }, { text: 'Actions' }],
      icons: {
        mdiClose: mdiClose,
        mdiAlertCircle: mdiAlertCircle,
        mdiMenu: mdiMenu,
      },  
      etsy_data_dialog: false,
      fetching_etsy_data: false,
      etsy_data: {},
      product_count: {},
      searchTerm: this.search,
      imageDifferenceDialog: false,
      loadingImages: false,
      imageData: {
        success: false,
        data: {
          etsy_images: [],
          shopify_images: [],
          etsy_image_count: 0,
          shopify_image_count: 0
        }
      },
    }
  },
  components: {
    JsonViewer,
  },
  mounted() {
    this.shop = store.getters.loggedInShop
    this.fetchEtsyProducts()
  },
  methods: {
    async fetchEtsyProducts() {
      try {
        this.is_loading = true
        this.$emit('updateSearch', this.searchTerm);
        let response = await axios.get(`/admin/shops/${this.shop.id}/get-etsy-products`, {
          params: {
            shop_id: this.shop.id,
            type: 3,
            page: this.products.current_page,
            search: this.searchTerm,
          },
        })

        if(response.data)
        {
          this.products = response.data
          this.product_count = response.data.product_count
          this.$emit('count-updated', this.product_count);
        }
      } catch (e) {
      } finally {
        this.is_loading = false
      }
    },

    handlePageChange(value) {
      this.fetchEtsyProducts()
    },

    async etsyProductDetail(etsyProductId)
    {
      router.push(`/etsy-products/${etsyProductId}`);
    },

    async shopifyProductDetail(shopifyId)
    {
      router.push(`/shopify-products/${shopifyId}`);
    },

    async getEtsyApiDetail(listing_id) {
      try {
        this.etsy_data_dialog = true
        this.fetching_etsy_data = true
        let response = await axios.get(`admin/shops/${this.shop.id}/etsy-listing/${listing_id}/get-raw-data`)
        // if (response.data.success) {
        this.etsy_data = response.data
        // }
      } catch (e) {
      } finally {
        this.fetching_etsy_data = false
      }
    },

    showSKUStatus(product)
    {
      let parent_data = product.upload_result;
      let prepared_data = product.data;

        let item = JSON.parse(parent_data);
        let prepared_item = JSON.parse(prepared_data ? prepared_data : "[]" );

        return `${item ? (item.has_variations ? "skus in variations" : item.skus[0]) : (prepared_item?.has_variations ? "sku in variations" : prepared_item?.skus)} <br/> ${item ? item.quantity : prepared_item?.quantity} in stock | ${item?.price.currency_code}
                ${item ? (item.price.amount / item.price.divisor) : prepared_item?.price} <br/> Status: ${product.etsy_state}`
    },
    
    async showImageDifference(item) {
      try {
        this.imageDifferenceDialog = true;
        this.loadingImages = true;
        this.imageData = {
          success: false,
          data: {
            etsy_images: [],
            shopify_images: [],
            etsy_image_count: 0,
            shopify_image_count: 0
          }
        };
        
        const response = await axios.get(`/admin/shops/${this.shop.id}/listing/${item.listing_id}/linked-product-images`);
        this.imageData = response.data;
      } catch (error) {
        console.error('Error fetching realtime image comparison:', error);
        this.imageData = {
          success: false,
          message: error.response?.data?.message || 'Failed to load realtime image comparison'
        };
      } finally {
        this.loadingImages = false;
      }
    },
  },
}
</script>
